import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_JS_FRONTEND_KEY;

const GoogleMapComponent = ({ primaryAddress, selectedAddress, facilities, loading, mapCenter, facilitiesLoading, isGoogleMapsLoaded, googleMapsLoadError }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const markersRef = useRef([]);
  const selectedAddressMarkerRef = useRef(null);
  const selectedAddressInfoWindowRef = useRef(null);
  const facilityInfoWindowsRef = useRef(new Map()); // Map to store facility InfoWindows by facility ID

  // Create AdvancedMarkerElement instances for primary address and facilities, and fit bounds
  const createAdvancedMarkers = useCallback(async (map) => {
    if (!map || !window.google || !window.google.maps) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker && marker.map !== undefined) {
        marker.map = null;
      }
    });
    markersRef.current = [];
    
    // Clear selected address marker and InfoWindow
    if (selectedAddressMarkerRef.current && selectedAddressMarkerRef.current.map !== undefined) {
      selectedAddressMarkerRef.current.map = null;
    }
    selectedAddressMarkerRef.current = null;
    if (selectedAddressInfoWindowRef.current) {
      selectedAddressInfoWindowRef.current.close();
    }
    selectedAddressInfoWindowRef.current = null;
    
    // Clear facility InfoWindows
    facilityInfoWindowsRef.current.forEach((infoWindow) => {
      if (infoWindow) {
        infoWindow.close();
      }
    });
    facilityInfoWindowsRef.current.clear();

    try {
      // Import the marker library
      const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');
      const bounds = new window.google.maps.LatLngBounds();

      // Create AdvancedMarkerElement for selected address with native InfoWindow
      if (selectedAddress && selectedAddress.latitude && selectedAddress.longitude) {
        const selectedPos = {
          lat: parseFloat(selectedAddress.latitude),
          lng: parseFloat(selectedAddress.longitude),
        };
        
        const selectedMarker = new AdvancedMarkerElement({
          position: selectedPos,
          map: map,
          title: selectedAddress.location_name || 'Selected Address',
        });

        selectedAddressMarkerRef.current = selectedMarker;
        markersRef.current.push(selectedMarker);
        bounds.extend(selectedPos);
        
        // Create native InfoWindow for selected address
        const infoWindowContent = document.createElement('div');
        infoWindowContent.innerHTML = `
          <div style="font-size: 14px; font-weight: bold;">
            ${selectedAddress.location_name || 'Selected Address'}
          </div>
        `;
        
        const infoWindow = new window.google.maps.InfoWindow({
          content: infoWindowContent,
          position: selectedPos,
        });
        
        selectedAddressInfoWindowRef.current = infoWindow;
        
        // Open InfoWindow automatically when address is selected
        infoWindow.open({
          anchor: selectedMarker,
          map: map,
        });
        
        // Toggle InfoWindow when marker is clicked
        selectedMarker.addListener('click', () => {
          if (selectedAddressInfoWindowRef.current) {
            if (selectedAddressInfoWindowRef.current.getMap()) {
              selectedAddressInfoWindowRef.current.close();
            } else {
              selectedAddressInfoWindowRef.current.open({
                anchor: selectedMarker,
                map: map,
              });
            }
          }
        });
      }

      // Create AdvancedMarkerElement for each facility
      const validFacilities = facilities.filter(
        facility => facility.address && facility.address.latitude && facility.address.longitude
      );

      for (const facility of validFacilities) {
        const facilityPosition = {
          lat: parseFloat(facility.address.latitude),
          lng: parseFloat(facility.address.longitude),
        };

        const marker = new AdvancedMarkerElement({
          position: facilityPosition,
          map: map,
          title: facility.name,
        });

        markersRef.current.push(marker);
        bounds.extend(facilityPosition);
        
        // Create InfoWindow for facility
        const facilityInfoWindowContent = document.createElement('div');
        const facilityId = facility.id; // Capture facility ID in closure
        facilityInfoWindowContent.innerHTML = `
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">
            ${facility.name || 'Facility'}
          </div>
          <a href="#" class="facility-link" data-facility-id="${facilityId}" style="color: #1976d2; text-decoration: none; font-size: 12px; cursor: pointer;">
            View Details
          </a>
        `;
        
        const facilityInfoWindow = new window.google.maps.InfoWindow({
          content: facilityInfoWindowContent,
          position: facilityPosition,
        });
        
        // Store InfoWindow reference using facility ID, or use index as fallback
        const facilityKey = facilityId || facility.name || markersRef.current.length - 1;
        facilityInfoWindowsRef.current.set(facilityKey, facilityInfoWindow);
        
        // Add click listener to the link for navigation
        // Use domready event to ensure the DOM is rendered before accessing elements
        facilityInfoWindow.addListener('domready', () => {
          // Find the link element using the data attribute
          const link = document.querySelector(`a[data-facility-id="${facilityId}"]`);
          if (link && facilityId) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              navigate(`/facilities/${facilityId}`);
            });
          }
        });
        
        // Add click listener to toggle InfoWindow
        marker.addListener('click', () => {
          // Close all other facility InfoWindows first
          facilityInfoWindowsRef.current.forEach((infoWindow, key) => {
            if (key !== facilityKey && infoWindow && infoWindow.getMap()) {
              infoWindow.close();
            }
          });
          
          // Toggle current facility InfoWindow
          if (facilityInfoWindow.getMap()) {
            facilityInfoWindow.close();
          } else {
            facilityInfoWindow.open({
              anchor: marker,
              map: map,
            });
          }
        });
      }

      // Auto-fit map to show all markers with padding
      if (validFacilities.length > 0 || (selectedAddress && selectedAddress.latitude && selectedAddress.longitude)) {
        map.fitBounds(bounds, { padding: 50 });
      }
    } catch (error) {
      console.error('Error creating advanced markers:', error);
    }
  }, [facilities, selectedAddress, navigate]);

  // Handle map load
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setIsMapReady(true);
    // Immediately try to create markers if data is available
    if (!facilitiesLoading && !loading && facilities.length > 0) {
      createAdvancedMarkers(map);
    }
  }, [facilities, facilitiesLoading, loading, createAdvancedMarkers]);

  // Reset isMapReady on unmount
  useEffect(() => {
    return () => {
      setIsMapReady(false);
      mapRef.current = null;
    };
  }, []);

  // Update markers when facilities or selected address changes, or when map becomes ready
  useEffect(() => {
    // Check mapRef.current directly - it's the source of truth for map readiness
    if (mapRef.current && !facilitiesLoading && !loading) {
      createAdvancedMarkers(mapRef.current);
    }
  }, [facilities, selectedAddress, facilitiesLoading, loading, isMapReady, createAdvancedMarkers]);

  const mapContainerStyle = {
    height: '100%',
    width: '100%',
  };

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      {loading ? (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.200',
          }}
        >
          <CircularProgress />
        </Box>
      ) : GOOGLE_MAPS_API_KEY ? (
        googleMapsLoadError ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.200',
            }}
          >
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Error loading Google Maps: {googleMapsLoadError.message}
            </Box>
          </Box>
        ) : !isGoogleMapsLoaded ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.200',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={12}
            options={mapOptions}
            onLoad={onMapLoad}
          >
            {/* Primary address and facility markers are rendered as AdvancedMarkerElement in createAdvancedMarkers */}
          </GoogleMap>
        )
      ) : (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.200',
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            Google Maps API key not configured
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GoogleMapComponent;
